import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { ContadoresDeRotas, PainelDeJobs, SaudeDetalhada } from '@arena/shared/schemas';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { credencialDeOperador, operacaoApi } from '../api/operacao.js';
import { Erro, EsqueletoDeCard, EsqueletoDeTabela } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { TemaToggle } from '../ui/TemaToggle.jsx';
import {
  Botao,
  Campo,
  Card,
  Kpi,
  Nota,
  Segmentado,
  Status,
  Tabela,
  Vazio,
} from '../ui/primitivos.jsx';
import { CabecalhoDoOperador } from './CabecalhoDoOperador.jsx';
import {
  duracao,
  haQuanto,
  idCurto,
  percentual,
  ROTULO_DO_ALERTA,
  situacaoDaFila,
  situacaoDaSaude,
  tempoNoAr,
} from './formatos.js';

/**
 * A tela do operador — saúde, painel de jobs com a fila morta e
 * contadores por rota (documento 20).
 *
 * "Restrito ao Dono, ou fora do aplicativo": fica FORA. Não mora no shell,
 * não aparece no menu e não usa a sessão de arena, porque o que ela
 * mostra é do processo e da fila — de todas as arenas. O Dono da Arena
 * Alpha não pode ver o job morto da Arena Beira-Mar. Quem entra aqui é
 * quem opera o sistema, com a credencial `OPERACAO_TOKEN`.
 *
 * Não existe no protótipo; é montada com os componentes do documento 05,
 * como o documento 20 pede. Atualiza sozinha a cada 30 segundos.
 */

const ATUALIZACAO_MS = 30_000;

type Saida = 'saiu' | 'recusada';

export function PaginaDeOperacao() {
  const cliente = useQueryClient();
  const [token, setToken] = useState<string | null>(() => credencialDeOperador.ler());
  const [recusada, setRecusada] = useState(false);

  if (!token) {
    return (
      <EntradaDoOperador
        recusada={recusada}
        onEntrar={(novo) => {
          credencialDeOperador.gravar(novo);
          setRecusada(false);
          setToken(novo);
        }}
      />
    );
  }

  return (
    <PainelDeOperacao
      token={token}
      onSair={(motivo: Saida) => {
        credencialDeOperador.limpar();
        // O que a credencial anterior leu não fica no cache para a próxima.
        cliente.removeQueries({ queryKey: ['operacao'] });
        setRecusada(motivo === 'recusada');
        setToken(null);
      }}
    />
  );
}

// ---------------------------------------------------------------------

/** A entrada pela credencial de operador — desta tela e da plataforma. */
export function EntradaDoOperador({
  recusada,
  onEntrar,
  titulo = 'Operação',
  descricao = 'Saúde, fila de jobs e rotas — de todas as arenas.',
}: {
  recusada: boolean;
  onEntrar: (token: string) => void;
  titulo?: string;
  descricao?: string;
}) {
  const [valor, setValor] = useState('');

  return (
    <div className="login">
      <section className="login-lado">
        <div className="login-topo">
          <span>Sand Sports · área do operador</span>
          <TemaToggle />
        </div>

        <div className="login-centro">
          <div className="login-box">
            <img className="logo" src="/brand/logo-com-nome.webp" alt="Sand Sports" />
            <h1>{titulo}</h1>
            <p>{descricao}</p>

            <form
              className="form"
              style={{ marginTop: 28, textAlign: 'left' }}
              onSubmit={(e) => {
                e.preventDefault();
                if (valor.trim()) onEntrar(valor.trim());
              }}
            >
              <Campo
                rotulo="Credencial de operador"
                ajuda="A OPERACAO_TOKEN do painel do Dokploy. Não é o login de uma arena."
              >
                <input
                  type="password"
                  autoComplete="off"
                  autoFocus
                  required
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                />
              </Campo>
              {recusada && <div className="erro">Credencial recusada.</div>}
              <Botao type="submit">Entrar</Botao>
            </form>

            <p className="hint">A credencial fica só nesta aba: fechar a aba a esquece.</p>
          </div>
        </div>

        <div className="login-rodape">
          <span>© {new Date().getFullYear()} Sand Sports</span>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------

function PainelDeOperacao({ token, onSair }: { token: string; onSair: (motivo: Saida) => void }) {
  const [horas, setHoras] = useState<'24' | '168'>('24');
  const [minutos, setMinutos] = useState<'15' | '60'>('15');

  const comum = { refetchInterval: ATUALIZACAO_MS, retry: false, staleTime: 0 } as const;
  const saude = useQuery({
    queryKey: ['operacao', 'saude'],
    queryFn: () => operacaoApi.saude(token),
    ...comum,
  });
  const jobs = useQuery({
    queryKey: ['operacao', 'jobs', horas],
    queryFn: () => operacaoApi.jobs(token, Number(horas)),
    ...comum,
  });
  const rotas = useQuery({
    queryKey: ['operacao', 'rotas', minutos],
    queryFn: () => operacaoApi.rotas(token, Number(minutos)),
    ...comum,
  });

  // A credencial recusada em qualquer uma das três volta para a entrada —
  // e só ela: um 500 aqui é informação, não motivo para sair.
  const recusada = [saude.error, jobs.error, rotas.error].some(
    (erro) => erro instanceof ErroDeApi && erro.status === 401,
  );
  useEffect(() => {
    if (recusada) onSair('recusada');
  }, [recusada, onSair]);

  // Recalculado a cada render — e há render a cada atualização das consultas.
  const agora = new Date();
  const processo = saude.data?.processo;

  return (
    <>
      <CabecalhoDoOperador titulo="Operação" onSair={() => onSair('saiu')} />
      <div className="main">
        <div className="page-head">
          <div>
            <h1>Operação</h1>
            <p>
              O processo, a fila e as rotas — de todas as arenas.
              {processo && (
                <>
                  {' '}
                  Réplica <b>{processo.hostname}</b>, no ar há {tempoNoAr(processo.uptimeSegundos)}.
                </>
              )}
            </p>
          </div>
          <div className="head-actions">
            {saude.data && (
              <Status situacao={situacaoDaSaude(saude.data).situacao}>
                {situacaoDaSaude(saude.data).rotulo}
              </Status>
            )}
            <Botao
              variante="contorno"
              onClick={() => {
                void saude.refetch();
                void jobs.refetch();
                void rotas.refetch();
              }}
            >
              Atualizar
            </Botao>
          </div>
        </div>

        <Nota>
          <b>Fora do aplicativo das arenas.</b> Os contadores de rota são da réplica que respondeu,
          em memória, desde que ela subiu; o painel de jobs vem da própria fila e vale para todas.
          Os procedimentos de cada alerta estão em <b>Docs/operacao/runbook.md</b>.
        </Nota>

        <SecaoDeSaude consulta={saude} />
        <SecaoDaFilaMorta consulta={jobs} agora={agora} />
        <SecaoDeJobs consulta={jobs} agora={agora} horas={horas} onHoras={setHoras} />
        <SecaoDeRotas consulta={rotas} minutos={minutos} onMinutos={setMinutos} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------

function falhaDaConsulta(consulta: UseQueryResult<unknown>, tentar: () => void) {
  const erro = consulta.error;
  return (
    <Erro
      tipo={erro instanceof ErroDeRede ? 'rede' : 'falha'}
      detalhe={mensagemDoErro(erro)}
      {...(erro instanceof ErroDeApi && erro.requestId ? { codigo: erro.requestId } : {})}
      onTentarDeNovo={tentar}
    />
  );
}

function SecaoDeSaude({ consulta }: { consulta: UseQueryResult<SaudeDetalhada> }) {
  if (consulta.isPending) return <EsqueletoDeCard linhas={4} />;
  if (consulta.isError) return falhaDaConsulta(consulta, () => void consulta.refetch());

  const { banco, migrations, fila, requisicoes, alertas } = consulta.data;
  const erros = [banco.erro, migrations.erro, fila.erro].filter(Boolean);

  return (
    <>
      {alertas.length > 0 && (
        <div className="grid" style={{ gap: 10 }}>
          {alertas.map((alerta) => (
            <div key={alerta} className="aviso conflito" role="alert">
              <Icone nome="info" />
              <div>{ROTULO_DO_ALERTA[alerta]}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid g4">
        <Kpi
          valor={banco.ok ? duracao(banco.latenciaMs) : 'fora'}
          rotulo="Banco · ida e volta"
          icone="box"
        />
        <Kpi
          valor={migrations.aplicadas}
          rotulo={
            migrations.pendentes.length > 0
              ? `Migrations aplicadas · ${migrations.pendentes.length} pendente(s)`
              : 'Migrations aplicadas'
          }
          icone="check"
        />
        <Kpi
          valor={fila.ligada ? fila.filaMorta : '—'}
          rotulo={
            fila.ligada ? 'Na fila morta' : `Fila ${fila.modo ?? 'antes do boot'} neste processo`
          }
          icone="clip"
        />
        <Kpi
          valor={duracao(requisicoes.p95Ms)}
          rotulo={`p95 · ${requisicoes.volume} req. em ${requisicoes.janelaMinutos} min · ${percentual(requisicoes.taxaDeErro)} de erro`}
          icone="chart"
        />
      </div>

      {(erros.length > 0 ||
        migrations.comFalha.length > 0 ||
        migrations.pendentes.length > 0 ||
        migrations.desconhecidas.length > 0) && (
        <Card titulo="Detalhes da verificação">
          <div className="grid" style={{ gap: 8, fontSize: 13 }}>
            {erros.map((erro) => (
              <div key={erro}>{erro}</div>
            ))}
            {migrations.pendentes.length > 0 && (
              <div>
                Pendentes: <span className="num">{migrations.pendentes.join(', ')}</span>
              </div>
            )}
            {migrations.comFalha.length > 0 && (
              <div>
                Com falha: <span className="num">{migrations.comFalha.join(', ')}</span>
              </div>
            )}
            {migrations.desconhecidas.length > 0 && (
              <div>
                No banco e não nesta imagem — o esperado logo depois de um rollback de aplicação:{' '}
                <span className="num">{migrations.desconhecidas.join(', ')}</span>
              </div>
            )}
          </div>
        </Card>
      )}
    </>
  );
}

function SecaoDaFilaMorta({
  consulta,
  agora,
}: {
  consulta: UseQueryResult<PainelDeJobs>;
  agora: Date;
}) {
  if (consulta.isPending) return <EsqueletoDeTabela linhas={3} colunas={6} />;
  if (consulta.isError) return null; // o erro aparece uma vez só, na seção de jobs

  const { filaMorta, ligada } = consulta.data;
  return (
    <Card
      titulo="Fila morta"
      dica={ligada ? `${filaMorta.pendentes} esperando alguém` : 'fila desligada neste processo'}
    >
      {filaMorta.itens.length === 0 ? (
        <Vazio>Nenhum job na fila morta.</Vazio>
      ) : (
        <Tabela
          colunas={['Fila de origem', 'Arena', 'Requisição', 'Morreu', 'Tentativas', 'Causa']}
        >
          {filaMorta.itens.map((job) => (
            <tr key={job.id}>
              <td className="num">{job.filaDeOrigem ?? '—'}</td>
              <td className="num" title={job.arenaId ?? undefined}>
                {idCurto(job.arenaId)}
              </td>
              <td className="num" title={job.requestId ?? undefined}>
                {idCurto(job.requestId)}
              </td>
              <td title={job.morreuEm}>{haQuanto(job.morreuEm, agora)}</td>
              <td className="num">{job.tentativas ?? '—'}</td>
              <td style={{ whiteSpace: 'normal', minWidth: 260 }}>{job.erro ?? '—'}</td>
            </tr>
          ))}
        </Tabela>
      )}
    </Card>
  );
}

function SecaoDeJobs({
  consulta,
  agora,
  horas,
  onHoras,
}: {
  consulta: UseQueryResult<PainelDeJobs>;
  agora: Date;
  horas: '24' | '168';
  onHoras: (horas: '24' | '168') => void;
}) {
  return (
    <Card
      titulo="Jobs por fila"
      acao={
        <Segmentado
          aria-label="Janela dos jobs"
          valor={horas}
          onChange={onHoras}
          opcoes={[
            { valor: '24', rotulo: '24 horas' },
            { valor: '168', rotulo: '7 dias' },
          ]}
        />
      }
    >
      {consulta.isPending ? (
        <EsqueletoDeTabela linhas={5} colunas={8} />
      ) : consulta.isError ? (
        falhaDaConsulta(consulta, () => void consulta.refetch())
      ) : consulta.data.filas.length === 0 ? (
        <Vazio>
          {consulta.data.ligada
            ? 'Nenhuma fila registrada.'
            : `A fila está ${consulta.data.modo ?? 'desligada'} neste processo.`}
        </Vazio>
      ) : (
        <Tabela
          colunas={[
            'Fila',
            'Situação',
            'Na fila',
            'Retentativa',
            'Rodando',
            'Concluídos',
            'Falhos',
            'Duração média',
            'p95',
            'Último sucesso',
            'Última falha',
          ]}
        >
          {consulta.data.filas.map((linha) => {
            const pilula = situacaoDaFila(linha);
            return (
              <tr key={linha.fila}>
                <td className="num">{linha.fila}</td>
                <td>
                  <Status situacao={pilula.situacao}>{pilula.rotulo}</Status>
                </td>
                <td className="num">{linha.naFila}</td>
                <td className="num">{linha.emRetentativa}</td>
                <td className="num">{linha.ativos}</td>
                <td className="num">{linha.concluidos}</td>
                <td className="num">{linha.falhos}</td>
                <td className="num">{duracao(linha.duracaoMediaMs)}</td>
                <td className="num">{duracao(linha.duracaoP95Ms)}</td>
                <td title={linha.ultimoSucessoEm ?? undefined}>
                  {haQuanto(linha.ultimoSucessoEm, agora)}
                </td>
                <td title={linha.ultimaFalhaEm ?? undefined}>
                  {haQuanto(linha.ultimaFalhaEm, agora)}
                </td>
              </tr>
            );
          })}
        </Tabela>
      )}
    </Card>
  );
}

function SecaoDeRotas({
  consulta,
  minutos,
  onMinutos,
}: {
  consulta: UseQueryResult<ContadoresDeRotas>;
  minutos: '15' | '60';
  onMinutos: (minutos: '15' | '60') => void;
}) {
  return (
    <Card
      titulo="Rotas"
      {...(consulta.data ? { dica: `réplica ${consulta.data.processo.hostname}` } : {})}
      acao={
        <Segmentado
          aria-label="Janela das rotas"
          valor={minutos}
          onChange={onMinutos}
          opcoes={[
            { valor: '15', rotulo: '15 min' },
            { valor: '60', rotulo: '1 hora' },
          ]}
        />
      }
    >
      {consulta.isPending ? (
        <EsqueletoDeTabela linhas={6} colunas={8} />
      ) : consulta.isError ? (
        falhaDaConsulta(consulta, () => void consulta.refetch())
      ) : consulta.data.rotas.length === 0 ? (
        <Vazio>Nenhuma requisição nesta janela.</Vazio>
      ) : (
        <Tabela
          colunas={['Rota', 'Volume', '4xx', '5xx', 'Taxa de erro', 'p95', 'Média', 'Máximo']}
        >
          {[consulta.data.total, ...consulta.data.rotas].map((linha, i) => (
            <tr key={linha.rota} style={i === 0 ? { fontWeight: 700 } : undefined}>
              <td className="num">{linha.rota}</td>
              <td className="num">{linha.volume}</td>
              <td className="num">{linha.porClasse['4xx']}</td>
              <td className="num">{linha.porClasse['5xx']}</td>
              <td className="num">{percentual(linha.taxaDeErro)}</td>
              <td className="num">{duracao(linha.p95Ms)}</td>
              <td className="num">{duracao(linha.mediaMs)}</td>
              <td className="num">{duracao(linha.maxMs)}</td>
            </tr>
          ))}
        </Tabela>
      )}
    </Card>
  );
}
